import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ConsultationProvider } from './context/ConsultationContext';
import { CategoryFilterProvider } from './context/CategoryFilterContext';
import { SettingsProvider } from './context/SettingsContext';
import PromotionalBanner from './components/common/PromotionalBanner';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/common/ScrollToTop';
import Home from './pages/Home';
import Nosotros from './pages/Nosotros';
import Login from './pages/Login';
import Register from './pages/Register';
import Consultation from './pages/Consultation';
import Products from './pages/Products';
import Categories from './pages/Categories';
import ProductDetail from './pages/ProductDetail';
import TermsAndConditions from './pages/TermsAndConditions';

import ProtectedRoute from './components/common/ProtectedRoute';
import './styles/globals.css';
import Contact from './pages/Contact';
import Search from './pages/Search';
import Profile from './pages/Profile';
import LibroReclamaciones from './pages/LibroReclamaciones';
import AdminPanel from './pages/AdminPanel';

import { useSettings } from './context/SettingsContext';
import MaintenanceModal from './components/common/MaintenanceModal';

// Hook personalizado para detectar dispositivos móviles
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

// Componente para manejar el layout
const AppLayout = () => {
  const { settings } = useSettings();
  const { user } = useAuth();
  const [showBanner, setShowBanner] = useState(true);
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isLoginPage = location.pathname === '/login';
  const isAdminUser = Boolean(user?.role === 'admin' || user?.is_staff);
  const isMobile = useIsMobile();

  // Modo de mantenimiento activo si la configuración de BD o la variable de entorno lo indican
  const isMaintenanceConfig = Boolean(
    String(settings.maintenance_mode || '').toLowerCase() === 'true' ||
    process.env.REACT_APP_MAINTENANCE_MODE === 'true'
  );

  // El mantenimiento solo bloquea a usuarios comunes en rutas públicas (no en /login ni /admin ni si el usuario es admin)
  const showMaintenance = isMaintenanceConfig && !isAdminUser && !isAdminRoute && !isLoginPage;

  // Verificar si hay banners cerrados en localStorage
  useEffect(() => {
    const checkBannerStatus = () => {
      const closed = localStorage.getItem('closedBanners');
      if (closed) {
        try {
          const closedBanners = JSON.parse(closed);
          setShowBanner(closedBanners.length === 0);
        } catch (error) {
          setShowBanner(true);
        }
      } else {
        setShowBanner(true);
      }
    };

    checkBannerStatus();
    // Escuchar cambios en localStorage
    const handleStorageChange = () => {
      checkBannerStatus();
    };
    window.addEventListener('storage', handleStorageChange);
    // También verificar periódicamente por cambios locales
    const interval = setInterval(checkBannerStatus, 1000);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Nuevo: callback para cerrar banner inmediatamente
  const handleBannerClose = () => {
    setShowBanner(false);
  };

  return (
    <div className="App" style={{ position: 'relative' }}>
      {/* Contenedor principal de la tienda: si está en mantenimiento, se desenfoca y desactiva clics, pero permite scroll nativo */}
      <div
        style={
          showMaintenance
            ? {
                filter: 'blur(5px) brightness(0.68)',
                WebkitFilter: 'blur(5px) brightness(0.68)',
                pointerEvents: 'none',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                transition: 'filter 0.3s ease, brightness 0.3s ease'
              }
            : undefined
        }
      >
        {!isAdminRoute && (
          <>
            <PromotionalBanner onClose={handleBannerClose} />
            <Header hasBanner={showBanner} />
          </>
        )}
        <main style={{
          minHeight: isAdminRoute ? '100vh' : 'calc(100vh - 140px)',
          marginTop: isAdminRoute ? '0' : (
            isMobile 
              ? (showBanner ? '80px' : '30px')  // Valores reducidos para móvil
              : (showBanner ? '110px' : '70px') // Valores originales para desktop
          )
        }}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/nosotros" element={<Nosotros />} />
            <Route path="/productos" element={<Products />} />
            <Route path="/producto/:id" element={<ProductDetail />} />
            <Route path="/categorias" element={<Categories />} />
            <Route path="/contacto" element={<Contact />} />
            <Route path="/search" element={<Search />} />
            <Route path="/perfil" element={<Profile />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/carrito" element={<Consultation />} />
            <Route path="/libro-reclamaciones" element={<LibroReclamaciones />} />
            <Route path="/admin" element={<ProtectedRoute adminOnly={false}><AdminPanel /></ProtectedRoute>} />
          </Routes>
        </main>
        {!isAdminRoute && <Footer />}
      </div>

      {/* Modal flotante de mantenimiento fijo en el centro de la pantalla */}
      {showMaintenance && <MaintenanceModal />}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <ConsultationProvider>
          <CategoryFilterProvider>
            <Router>
              <ScrollToTop />
              <AppLayout />
            </Router>
          </CategoryFilterProvider>
        </ConsultationProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;
